<template>
  <div class="content-dashboard">
    <div class="d-flex align-items-center justify-content-between">
      <div>
        <h3>{{ $t("loyalty_offer.title") }}</h3>
        <p>{{ $t("loyalty_offer.intro") }}</p>
      </div>
      <button class="btn btn-orange" @click="openModal">
        <img :src="plus" class="width-15" /> {{ $t("loyalty_offer.create_button") }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingOffers" class="row mt-5">
      <div class="col-lg-12">
        <div class="box-chart mb-3 w-100 text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
          <p class="mt-3">Chargement des offres...</p>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="offersLoadError" class="row mt-5">
      <div class="col-lg-12">
        <div class="box-chart mb-3 w-100">
          <div class="alert alert-warning text-center">
            <p class="mb-3">Erreur lors du chargement des offres. Veuillez réessayer.</p>
            <button class="btn btn-outline-warning" @click="fetchOffers">Réessayer</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="offres.length === 0" class="row mt-5">
      <div class="col-lg-12">
        <div class="box-chart mb-3 w-100 text-center py-5">
          <p class="mb-4">Aucune offre de fidélité trouvée.</p>
          <button class="btn btn-orange" @click="openModal">
            <img :src="plus" class="width-15" /> Créer votre première offre
          </button>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div v-else class="row mt-5">
      <div class="col-lg-12">
        <div class="box-chart mb-3 w-100">
          <div class="table-responsive mt-4">
            <table class="table table-style-1">
              <thead>
                <tr>
                  <th>
                    <strong>{{ $t("loyalty_offer.table.name") }}</strong>
                  </th>
                  <th>
                    <strong>{{ $t("loyalty_offer.table.period") }}</strong>
                  </th>
                  <th>
                    <strong>{{ $t("loyalty_offer.table.promo_type") }}</strong>
                  </th>
                  <th>
                    <strong>{{ $t("loyalty_offer.table.value") }}</strong>
                  </th>
                  <th><strong>Statut</strong></th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="offre in offres" :key="offre.id">
                  <td class="w-25 blue-color font-bold">{{ offre.nom }}</td>
                  <td>{{ offre.periode }}</td>
                  <td>{{ offre.promo }}</td>
                  <td>{{ offre.valeur }}</td>
                  <td>
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="`switch-${offre.id}`"
                        v-model="offre.active"
                        @change="toggleOfferStatus(offre)"
                      />
                      <label class="form-check-label small" :for="`switch-${offre.id}`">
                        {{ offre.active ? "Active" : "Inactive" }}
                      </label>
                    </div>
                  </td>
                  <td>
                    <button class="btn-save" @click="editOffer(offre)">
                      <i class="ti ti-pencil-alt"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div
      class="modal fade"
      :class="{ 'show d-block': showModal }"
      v-if="showModal"
      tabindex="-1"
      aria-labelledby="modalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered max-width-65">
        <div class="modal-content">
          <div class="modal-header justify-content-end">
            <button type="button" class="close-link" @click="closeModal" aria-label="Fermer">
              <i class="ti ti-close"></i>
            </button>
          </div>
          <div class="modal-body padd-modal">
            <h3 class="font-18">{{ isEditing ? "Modifier l'offre" : $t("loyalty_offer.create_title") }}</h3>

            <form @submit.prevent="handleSubmit" class="mt-5">
              <!-- Offer Name -->
              <div class="row">
                <div class="col-md-6">
                  <div class="input-form w-100">
                    <label class="label-control">{{ $t("loyalty_offer.form.name") }}</label>
                    <input
                      type="text"
                      class="form-control round-20"
                      v-model="formData.name"
                      required
                      placeholder="Nom de l'offre"
                    />
                    <p v-if="errors.name" class="text-danger small mt-2">{{ errors.name }}</p>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="input-form w-100">
                    <label class="label-control">Description de l'offre pour votre client</label>
                    <input
                      type="text"
                      class="form-control round-20"
                      v-model="formData.descriptionOffre"
                      placeholder="Bénéficiez de ..."
                    />
                  </div>
                </div>
              </div>

              <!-- Display Option -->
              <div class="input-form w-100 mt-4">
                <label class="label-control">Afficher l'offre pour vos clients</label>
                <div class="d-flex gap-4 mt-2">
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="radio"
                      id="display-oui"
                      :value="true"
                      v-model="formData.displayOffre"
                    />
                    <label class="form-check-label" for="display-oui">Oui</label>
                  </div>
                  <div class="form-check">
                    <input
                      class="form-check-input"
                      type="radio"
                      id="display-non"
                      :value="false"
                      v-model="formData.displayOffre"
                    />
                    <label class="form-check-label" for="display-non">Non</label>
                  </div>
                </div>
                <p class="field-hint">Si non, l'offre ne sera pas visible dans la page d'accueil de vos clients</p>
              </div>


              <!-- Rules Section - Vertical Layout -->
              <div class="rules-section mt-4">
                <div class="section-header">
                  <h5 class="section-title blue-color">Règles de l'offre</h5>
                  <p class="section-subtitle">Définissez les conditions d'application de l'offre</p>
                </div>
                <p v-if="errors.regles" class="text-danger small mt-2">{{ errors.regles }}</p>

                <!-- Multiple Rules Selection -->
                <div class="input-form w-100 mt-3">
                  <label class="label-control">Types de règles appliquées</label>
                  <div class="tag-input-wrapper">
                    <div class="tags" v-if="formData.regles.length">
                      <span v-for="(regle, index) in formData.regles" :key="index" class="tag">
                        {{ getRuleLabel(regle) }}
                        <span class="tag-remove" @click="removeRegle(index)">×</span>
                      </span>
                    </div>
                    <select class="form-select round-20" @change="addRegle($event.target.value)">
                      <option value="">Ajouter une règle...</option>
                      <option
                        v-for="rule in availableRules"
                        :key="rule.value"
                        :value="rule.value"
                        :disabled="formData.regles.includes(rule.value)"
                      >
                        {{ rule.label }}
                      </option>
                    </select>
                  </div>
                </div>

                <!-- Dynamic Fields for Rules - Vertical Stack -->
                <div class="rules-fields mt-4">
                  <!-- Clients: Multiple Select with Tags -->
                  <div v-if="formData.regles.includes('clients')" class="rule-field-group">
                    <div class="input-form w-100">
                      <label class="label-control">Sélection des clients</label>
                      <div class="tag-input-wrapper">
                        <div class="tags" v-if="formData.clients.length">
                          <span v-for="(clientId, idx) in formData.clients" :key="idx" class="tag">
                            {{ getClientDisplayName(clientId) }}
                            <span class="tag-remove" @click="removeClient(idx)">×</span>
                          </span>
                        </div>
                        <select
                          class="form-select round-20"
                          @change="addClient($event.target.value)"
                          :disabled="isLoadingClients"
                        >
                          <option value="">
                            {{ getClientsDropdownText() }}
                          </option>
                          <option
                            v-for="client in availableClients"
                            :key="client.id"
                            :value="client.id"
                            :disabled="formData.clients.includes(client.id)"
                          >
                            {{ getClientDisplayName(client) }}
                          </option>
                        </select>
                      </div>
                      <p class="field-hint">Sélectionnez un ou plusieurs clients concernés par cette offre</p>
                      <p v-if="errors.clients" class="text-danger small mt-2">{{ errors.clients }}</p>
                      <div v-if="clientsLoadError" class="text-danger small mt-2">
                        Erreur lors du chargement des clients. Veuillez réessayer.
                        <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="fetchClients">
                          Réessayer
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Cumul Fidelité -->
                  <div v-if="formData.regles.includes('notion_cumul')" class="rule-field-group">
                    <!-- Gray background box for Cumul options -->
                    <div class="p-3 bg-light rounded-20 mb-3">
                      <!-- Nombre déclencheur -->
                      <div class="input-form w-100">
                        <label class="label-control required">Nombre déclencheur</label>
                        <p class="field-hint mb-1">
                          Définissez le nombre de produits achetés nécessaires pour déclencher l'offre
                        </p>
                        <input
                          type="number"
                          class="form-control round-20"
                          v-model="formData.cumulNombreDeclencheur"
                          min="1"
                          placeholder="1"
                        />
                        <p v-if="errors.cumulNombreDeclencheur" class="text-danger small mt-2">
                          {{ errors.cumulNombreDeclencheur }}
                        </p>
                      </div>

                      <!-- Type de cumul -->
                      <div class="input-form w-100 mt-3">
                        <label class="label-control required">Sélection du type de cumul</label>
                        <div class="d-flex gap-4 mt-2">
                          <div class="form-check">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              id="cumul-quantites"
                              :checked="formData.cumulType === 'quantites'"
                              @change="formData.cumulType = 'quantites'"
                            />
                            <label class="form-check-label" for="cumul-quantites">Quantités commandés</label>
                          </div>
                          <div class="form-check">
                            <input
                              class="form-check-input"
                              type="checkbox"
                              id="cumul-unique"
                              :checked="formData.cumulType === 'unique'"
                              @change="formData.cumulType = 'unique'"
                            />
                            <label class="form-check-label" for="cumul-unique">Produits uniques commandés</label>
                          </div>
                        </div>

                        <div class="mt-2 text-muted small fst-italic">
                          <p v-if="formData.cumulType === 'quantites'">
                            "Quantités commandés" : l'offre sera déclenchée si X quantités sont commandées, que ce soit
                            X quantité(s) d'un même produit ou non. Par exemple 5 navitol compteront pour 5 quantités
                            commandées.
                          </p>
                          <p v-if="formData.cumulType === 'unique'">
                            "Produits uniques commandés" : l'offre sera déclenchée uniquement si X produits différents
                            ont été commandés, sans tenir compte des quantités d'un même produit. Par exemple 5 navitol
                            ne compteront que pour 1 produit.
                          </p>
                        </div>
                      </div>

                      <!-- Filtres de l'offre -->
                      <div class="mt-4">
                        <h6 class="font-bold">Filtres de l'offre</h6>

                        <!-- Par clients -->
                        <div class="input-form w-100 mt-2">
                          <label class="label-control">Par clients</label>
                          <div class="tag-input-wrapper">
                            <div class="tags" v-if="formData.cumulClients.length">
                              <span v-for="(clientId, idx) in formData.cumulClients" :key="idx" class="tag">
                                {{ getClientDisplayName(clientId) }}
                                <span class="tag-remove" @click="removeCumulClient(idx)">×</span>
                              </span>
                            </div>
                            <select
                              class="form-select round-20"
                              @change="addCumulClient($event.target.value)"
                              :disabled="isLoadingClients"
                            >
                              <option value="">
                                {{ getClientsDropdownText() }}
                              </option>
                              <option
                                v-for="client in availableClients"
                                :key="client.id"
                                :value="client.id"
                                :disabled="formData.cumulClients.includes(client.id)"
                              >
                                {{ getClientDisplayName(client) }}
                              </option>
                            </select>
                          </div>
                        </div>

                        <!-- Par espèce (Gamme) -->
                        <div class="input-form w-100 mt-2">
                          <label class="label-control">Par espèce</label>
                          <div class="tag-input-wrapper">
                            <div class="tags" v-if="formData.cumulGammes.length">
                              <span v-for="(gamme, idx) in formData.cumulGammes" :key="idx" class="tag">
                                {{ gamme }}
                                <span class="tag-remove" @click="removeCumulGamme(idx)">×</span>
                              </span>
                            </div>
                            <select class="form-select round-20" @change="addCumulGamme($event.target.value)">
                              <option value="">Sélectionner une espèce...</option>
                              <option
                                v-for="gamme in availableGammesOptions"
                                :key="gamme"
                                :value="gamme"
                                :disabled="formData.cumulGammes.includes(gamme)"
                              >
                                {{ gamme }}
                              </option>
                            </select>
                          </div>
                        </div>

                        <!-- Par gamme (Sous-gamme) -->
                        <div class="input-form w-100 mt-2">
                          <label class="label-control">Par gamme</label>
                          <div class="tag-input-wrapper">
                            <div class="tags" v-if="formData.cumulSousGammes.length">
                              <span v-for="(sousGamme, idx) in formData.cumulSousGammes" :key="idx" class="tag">
                                {{ sousGamme }}
                                <span class="tag-remove" @click="removeCumulSousGamme(idx)">×</span>
                              </span>
                            </div>
                            <select class="form-select round-20" @change="addCumulSousGamme($event.target.value)">
                              <option value="">Sélectionner une gamme...</option>
                              <option
                                v-for="sousGamme in availableSousGammesOptions"
                                :key="sousGamme"
                                :value="sousGamme"
                                :disabled="formData.cumulSousGammes.includes(sousGamme)"
                              >
                                {{ sousGamme }}
                              </option>
                            </select>
                          </div>
                        </div>

                        <!-- Par produit -->
                        <div class="input-form w-100 mt-2">
                          <label class="label-control">Par produit</label>
                          <div class="custom-select-wrapper">
                            <div
                              class="custom-select round-20"
                              :class="{ disabled: isLoadingCumulProduits }"
                              @click="toggleProductDropdown('cumulProduit')"
                            >
                              <span class="selected-option">
                                {{ getSelectedProductDisplay("cumulProduit") }}
                              </span>
                              <span class="arrow" :class="{ open: showProductDropdown.cumulProduit }"
                                ><img :src="iconDropDown"
                              /></span>
                            </div>
                            <div
                              v-if="showProductDropdown.cumulProduit"
                              class="custom-select-options text-start"
                              @scroll="handleProductScroll('cumulProduit', $event)"
                            >
                              <div class="search-container">
                                <input
                                  type="text"
                                  v-model="searchQueries.cumulProduit"
                                  placeholder="Rechercher un produit..."
                                  class="form-control search-input"
                                  @input="debouncedSearchProducts('cumulProduit')"
                                />
                              </div>
                              <div
                                class="option"
                                v-for="produit in filteredCumulProduits"
                                :key="produit.code"
                                @click="selectProduct(produit.id, 'cumulProduit')"
                              >
                                {{ produit.name }}
                              </div>
                              <div v-if="isLoadingCumulProduits" class="option loading">Chargement...</div>
                              <div
                                v-if="
                                  !hasMoreCumulProduits && filteredCumulProduits.length > 0 && !isLoadingCumulProduits
                                "
                                class="option info"
                              >
                                Fin de la liste
                              </div>
                              <div
                                v-if="filteredCumulProduits.length === 0 && !isLoadingCumulProduits"
                                class="option info"
                              >
                                Aucun produit trouvé
                              </div>
                            </div>
                          </div>
                        </div>

                        <!-- Par article -->
                        <div class="input-form w-100 mt-2">
                          <label class="label-control">Par article</label>
                          <div class="custom-select-wrapper">
                            <div
                              class="custom-select round-20"
                              :class="{ disabled: isLoadingVariants }"
                              @click="toggleVariantDropdown('cumulVariant')"
                            >
                              <span class="selected-option">
                                {{ getSelectedVariantDisplay("cumulVariant") }}
                              </span>
                              <span class="arrow" :class="{ open: showVariantDropdown.cumulVariant }"
                                ><img :src="iconDropDown"
                              /></span>
                            </div>
                            <div v-if="showVariantDropdown.cumulVariant" class="custom-select-options text-start">
                              <div class="search-container">
                                <input
                                  type="text"
                                  v-model="variantSearchTemp"
                                  placeholder="Rechercher un article..."
                                  class="form-control search-input"
                                  @input="debouncedVariantSearch($event.target.value)"
                                  @click.stop
                                />
                              </div>
                              <div
                                class="option"
                                v-for="variant in availableVariants"
                                :key="variant.id"
                                @click="selectVariant(variant.id, 'cumulVariant')"
                              >
                                {{ variant.name }}
                              </div>
                              <div v-if="isLoadingVariants" class="option loading">Chargement des articles...</div>
                              <div v-if="availableVariants.length === 0 && !isLoadingVariants" class="option info">
                                {{ variantSearchQuery ? "Aucun article trouvé" : "Aucun article disponible" }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Actions Section - Vertical Layout -->
              <div class="actions-section mt-5">
                <div class="section-header">
                  <h5 class="section-title blue-color">Actions de l'offre</h5>
                  <p class="section-subtitle">Définissez la récompense ou la réduction appliquée</p>
                </div>
                <p v-if="errors.action" class="text-danger small mt-2">{{ errors.action }}</p>

                <!-- Action Type Selection -->
                <div class="input-form w-100 mt-3">
                  <label class="label-control">Type d'action</label>
                  <select class="form-select round-20" v-model="formData.action">
                    <option value="">Choisir une action...</option>
                    <!-- <option value="montant">Remise en montant fixe</option> -->
                    <option value="pourcentage">Remise en pourcentage</option>
                    <!-- <option value="cadeau">Produit cadeau</option> -->
                  </select>
                </div>

                <!-- Action Filters Section -->
                <div v-if="(formData.action === 'pourcentage' || formData.action === 'montant') && !formData.regles.includes('notion_cumul')" class="action-filters mt-4">
                  <h6 class="font-bold blue-color">Filtres de l'action</h6>
                  <p class="section-subtitle mb-3">Définissez sur quels produits s'applique la réduction</p>

                  <!-- Par espèce (Gamme) -->
                  <div class="input-form w-100 mt-2">
                    <label class="label-control">Par espèce</label>
                    <div class="tag-input-wrapper">
                      <div class="tags" v-if="formData.actionGammes.length">
                        <span v-for="(gamme, idx) in formData.actionGammes" :key="idx" class="tag">
                          {{ gamme }}
                          <span class="tag-remove" @click="removeActionGamme(idx)">×</span>
                        </span>
                      </div>
                      <select class="form-select round-20" @change="addActionGamme($event.target.value)">
                        <option value="">Sélectionner une espèce...</option>
                        <option
                          v-for="gamme in availableGammesOptions"
                          :key="gamme"
                          :value="gamme"
                          :disabled="formData.actionGammes.includes(gamme)"
                        >
                          {{ gamme }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <!-- Par gamme (Sous-gamme) -->
                  <div class="input-form w-100 mt-2">
                    <label class="label-control">Par gamme</label>
                    <div class="tag-input-wrapper">
                      <div class="tags" v-if="formData.actionSousGammes.length">
                        <span v-for="(sousGamme, idx) in formData.actionSousGammes" :key="idx" class="tag">
                          {{ sousGamme }}
                          <span class="tag-remove" @click="removeActionSousGamme(idx)">×</span>
                        </span>
                      </div>
                      <select class="form-select round-20" @change="addActionSousGamme($event.target.value)">
                        <option value="">Sélectionner une gamme...</option>
                        <option
                          v-for="sousGamme in availableSousGammesOptions"
                          :key="sousGamme"
                          :value="sousGamme"
                          :disabled="formData.actionSousGammes.includes(sousGamme)"
                        >
                          {{ sousGamme }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <!-- Par produit -->
                  <div class="input-form w-100 mt-2">
                    <label class="label-control">Par produit</label>
                    <div class="custom-select-wrapper">
                      <div
                        class="custom-select round-20"
                        :class="{ disabled: isLoadingActionProduits }"
                        @click="toggleProductDropdown('actionProduit')"
                      >
                        <span class="selected-option">
                          {{ getSelectedProductDisplay('actionProduit') }}
                        </span>
                        <span class="arrow" :class="{ open: showProductDropdown.actionProduit }"
                          ><img :src="iconDropDown"
                        /></span>
                      </div>
                      <div
                        v-if="showProductDropdown.actionProduit"
                        class="custom-select-options text-start"
                        @scroll="handleProductScroll('actionProduit', $event)"
                      >
                        <div class="search-container">
                          <input
                            type="text"
                            v-model="searchQueries.actionProduit"
                            placeholder="Rechercher un produit..."
                            class="form-control search-input"
                            @input="debouncedSearchProducts('actionProduit')"
                          />
                        </div>
                        <div
                          class="option"
                          v-for="produit in filteredActionProduits"
                          :key="produit.code"
                          @click="selectProduct(produit.code, 'actionProduit')"
                        >
                          {{ produit.name }}
                        </div>
                        <div v-if="isLoadingActionProduits" class="option loading">Chargement...</div>
                        <div
                          v-if="!hasMoreActionProduits && filteredActionProduits.length > 0 && !isLoadingActionProduits"
                          class="option info"
                        >
                          Fin de la liste
                        </div>
                        <div v-if="filteredActionProduits.length === 0 && !isLoadingActionProduits" class="option info">
                          Aucun produit trouvé
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Par article -->
                  <div class="input-form w-100 mt-2">
                    <label class="label-control">Par article</label>
                    <div class="custom-select-wrapper">
                      <div
                        class="custom-select round-20"
                        :class="{ disabled: isLoadingVariants }"
                        @click="toggleVariantDropdown('actionVariant')"
                      >
                        <span class="selected-option">
                          {{ getSelectedVariantDisplay('actionVariant') }}
                        </span>
                        <span class="arrow" :class="{ open: showVariantDropdown.actionVariant }"
                          ><img :src="iconDropDown"
                        /></span>
                      </div>
                      <div v-if="showVariantDropdown.actionVariant" class="custom-select-options text-start">
                        <div class="search-container">
                          <input
                            type="text"
                            v-model="variantSearchTemp"
                            placeholder="Rechercher un article..."
                            class="form-control search-input"
                            @input="debouncedVariantSearch($event.target.value)"
                            @click.stop
                          />
                        </div>
                        <div
                          class="option"
                          v-for="variant in availableVariants"
                          :key="variant.id"
                          @click="selectVariant(variant.id, 'actionVariant')"
                        >
                          {{ variant.name }}
                        </div>
                        <div v-if="isLoadingVariants" class="option loading">Chargement des articles...</div>
                        <div v-if="availableVariants.length === 0 && !isLoadingVariants" class="option info">
                          {{ variantSearchQuery ? "Aucun article trouvé" : "Aucun article disponible" }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Conditional fields based on action selection -->
                <div v-if="formData.action" class="action-fields mt-4">
                  <!-- Montant Action -->
                  <div v-if="formData.action === 'montant'" class="action-field-group">
                    <div class="input-form w-100">
                      <label class="label-control">Montant de la réduction</label>
                      <div class="input-with-symbol">
                        <input
                          type="number"
                          class="form-control round-20"
                          v-model="formData.montant"
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                        />
                        <span class="input-symbol">€</span>
                      </div>
                      <p class="field-hint">Entrez le montant de la réduction en euros</p>
                    </div>
                  </div>

                  <!-- Pourcentage Action -->
                  <div v-else-if="formData.action === 'pourcentage'" class="action-field-group">
                    <div class="input-form w-100">
                      <label class="label-control required">Pourcentage de réduction</label>
                      <div class="input-with-symbol">
                        <select
                          v-if="isPercentageSelect"
                          class="form-select round-20"
                          v-model="formData.pourcentage"
                          required
                        >
                          <option value="">Sélectionner un pourcentage...</option>
                          <option value="5">5%</option>
                          <option value="10">10%</option>
                          <option value="15">15%</option>
                          <option value="20">20%</option>
                        </select>
                        <input
                          v-else
                          type="number"
                          class="form-control round-20"
                          v-model="formData.pourcentage"
                          placeholder="0"
                          min="1"
                          max="100"
                          required
                        />
                        <span class="input-symbol">%</span>
                      </div>
                      <p class="field-hint">
                        {{ isPercentageSelect ? "Sélectionnez le pourcentage de réduction" : "Entrez le pourcentage de réduction" }}
                      </p>
                      <p class="text-info-emphasis mt-2">
                        <strong>Important :</strong> veillez à ne pas paramétrer de réduction supérieure à vos
                        coefficients de marge
                      </p>
                    </div>
                  </div>

                  <!-- Cadeau Action -->
                  <div v-else-if="formData.action === 'cadeau'" class="action-field-group">
                    <!-- Configuration du produit cadeau -->
                    <!-- Configuration du produit cadeau -->
                    <div class="cadeau-configuration mt-4">
                      <!-- Simplified UI for Cumul Fidelité -->
                      <div v-if="formData.regles.includes('notion_cumul')">
                        <!-- Article gagné (Variant Search) -->
                        <div class="input-form w-100">
                          <label class="label-control required">Article gagné</label>
                          <div class="custom-select-wrapper">
                            <div
                              class="custom-select round-20"
                              :class="{ disabled: isLoadingVariants }"
                              @click="toggleVariantDropdown('cadeauVariantGagne')"
                            >
                              <span class="selected-option">
                                {{ getSelectedVariantDisplay("cadeauVariantGagne") }}
                              </span>
                              <span class="arrow" :class="{ open: showVariantDropdown.cadeauVariantGagne }"
                                ><img :src="iconDropDown"
                              /></span>
                            </div>
                            <div v-if="showVariantDropdown.cadeauVariantGagne" class="custom-select-options">
                              <div class="search-container">
                                <input
                                  type="text"
                                  v-model="variantSearchTemp"
                                  placeholder="Rechercher un article..."
                                  class="form-control search-input"
                                  @input="debouncedVariantSearch($event.target.value)"
                                  @click.stop
                                />
                              </div>
                              <div
                                class="option"
                                v-for="variant in availableVariants"
                                :key="variant.id"
                                @click="
                                  selectVariant(variant.id, 'cadeauVariantGagne');
                                  formData.cadeauArticleGagne = 'autre';
                                "
                              >
                                {{ variant.name }}
                              </div>
                              <div v-if="isLoadingVariants" class="option loading">Chargement des articles...</div>
                              <div v-if="availableVariants.length === 0 && !isLoadingVariants" class="option info">
                                {{ variantSearchQuery ? "Aucun article trouvé" : "Aucun article disponible" }}
                              </div>
                            </div>
                          </div>
                          <p v-if="errors.cadeauVariantGagne" class="text-danger small mt-2">
                            {{ errors.cadeauVariantGagne }}
                          </p>
                        </div>

                        <!-- Quantité gagnée -->
                        <div class="input-form w-100 mt-3">
                          <label class="label-control required">Quantité gagnée</label>
                          <input
                            type="number"
                            class="form-control round-20"
                            v-model="formData.cadeauQuantiteGagnee"
                            placeholder="1"
                            min="1"
                            required
                          />
                          <p class="field-hint">Nombre d'unités offertes</p>
                          <p v-if="errors.cadeauQuantiteGagnee" class="text-danger small mt-2">
                            {{ errors.cadeauQuantiteGagnee }}
                          </p>
                        </div>

                        <!-- Peut être supprimé du panier -->
                        <div class="toggle-group mt-4 col-4">
                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <label class="label-control required mb-0">Peut être supprimé du panier ?</label>
                            </div>
                            <div class="form-check form-switch">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="formData.cadeauSupprimablePanier"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <!-- Standard UI for other rules -->
                      <div v-else>
                        <!-- Déclencheur -->
                        <div class="input-form w-100">
                          <label class="label-control required">Déclencheur</label>
                          <select class="form-select round-20" v-model="formData.cadeauDeclencheur" required>
                            <option value="">Sélectionner un déclencheur...</option>
                            <option value="all">Tout le catalogue</option>
                            <option value="taxon">Espèce</option>
                            <option value="product">Produit</option>
                            <option value="variants">Articles</option>
                          </select>
                          <p v-if="errors.cadeauDeclencheur" class="text-danger small mt-2">
                            {{ errors.cadeauDeclencheur }}
                          </p>
                        </div>

                        <!-- Champs conditionnels basés sur le déclencheur -->
                        <div v-if="formData.cadeauDeclencheur === 'taxon'" class="input-form w-100 mt-3">
                          <label class="label-control required">Sélection de l'espèce</label>
                          <select class="form-select round-20" v-model="formData.cadeauTaxon">
                            <option value="">Sélectionner un espèce...</option>
                            <option v-for="taxon in availableTaxons" :key="taxon.id" :value="taxon.id">
                              {{ taxon.name }}
                            </option>
                          </select>
                          <p v-if="errors.cadeauTaxon" class="text-danger small mt-2">{{ errors.cadeauTaxon }}</p>
                        </div>

                        <div v-else-if="formData.cadeauDeclencheur === 'product'" class="input-form w-100 mt-3">
                          <label class="label-control required">Sélection du produit</label>
                          <div class="custom-select-wrapper">
                            <div
                              class="custom-select round-20"
                              :class="{ disabled: isLoadingCadeauProduits }"
                              @click="toggleProductDropdown('cadeauProduitDeclencheur')"
                            >
                              <span class="selected-option">
                                {{ getSelectedProductDeclencheurDisplay("cadeauProduitDeclencheur") }}
                              </span>
                              <span class="arrow" :class="{ open: showProductDropdown.cadeauProduitDeclencheur }"
                                ><img :src="iconDropDown"
                              /></span>
                            </div>
                            <div
                              v-if="showProductDropdown.cadeauProduitDeclencheur"
                              class="custom-select-options"
                              @scroll="handleProductScroll('cadeauProduitDeclencheur', $event)"
                            >
                              <div class="search-container">
                                <input
                                  type="text"
                                  v-model="searchQueries.cadeauProduitDeclencheur"
                                  placeholder="Rechercher un produit..."
                                  class="form-control search-input"
                                  @input="debouncedSearchProducts('cadeauProduitDeclencheur')"
                                />
                              </div>
                              <div
                                class="option"
                                v-for="produit in filteredCadeauProduits"
                                :key="produit.id"
                                @click="selectProduct(produit.id, 'cadeauProduitDeclencheur')"
                              >
                                {{ produit.name }}
                              </div>
                              <div v-if="isLoadingCadeauProduits" class="option loading">Chargement...</div>
                              <div
                                v-if="
                                  !hasMoreCadeauProduits &&
                                  filteredCadeauProduits.length > 0 &&
                                  !isLoadingCadeauProduits
                                "
                                class="option info"
                              >
                                Fin de la liste
                              </div>
                              <div
                                v-if="filteredCadeauProduits.length === 0 && !isLoadingCadeauProduits"
                                class="option info"
                              >
                                Aucun produit trouvé
                              </div>
                            </div>
                          </div>
                          <p v-if="errors.cadeauProduitDeclencheur" class="text-danger small mt-2">
                            {{ errors.cadeauProduitDeclencheur }}
                          </p>
                          <div v-if="cadeauProduitsLoadError" class="text-danger small mt-2">
                            Erreur lors du chargement des produits. Veuillez réessayer.
                            <button
                              type="button"
                              class="btn btn-link btn-sm p-0 ms-1"
                              @click="fetchProducts(1, 'cadeauProduitDeclencheur', false)"
                            >
                              Réessayer
                            </button>
                          </div>
                        </div>

                        <!-- Variant Declencheur with Search -->
                        <div v-else-if="formData.cadeauDeclencheur === 'variants'" class="input-form w-100 mt-3">
                          <label class="label-control required">Sélection de l'article déclencheur</label>
                          <div class="custom-select-wrapper">
                            <div
                              class="custom-select round-20"
                              :class="{ disabled: isLoadingVariants }"
                              @click="toggleVariantDropdown('cadeauVariantDeclencheur')"
                            >
                              <span class="selected-option">
                                {{ getSelectedVariantDisplay("cadeauVariantDeclencheur") }}
                              </span>
                              <span class="arrow" :class="{ open: showVariantDropdown.cadeauVariantDeclencheur }"
                                ><img :src="iconDropDown"
                              /></span>
                            </div>
                            <div v-if="showVariantDropdown.cadeauVariantDeclencheur" class="custom-select-options">
                              <div class="search-container">
                                <input
                                  type="text"
                                  v-model="variantSearchTemp"
                                  placeholder="Rechercher un article..."
                                  class="form-control search-input"
                                  @input="debouncedVariantSearch($event.target.value)"
                                  @click.stop
                                />
                              </div>
                              <div
                                class="option"
                                v-for="variant in availableVariants"
                                :key="variant.id"
                                @click="selectVariant(variant.id, 'cadeauVariantDeclencheur')"
                              >
                                {{ variant.name }}
                              </div>
                              <div v-if="isLoadingVariants" class="option loading">Chargement des articles...</div>
                              <div v-if="availableVariants.length === 0 && !isLoadingVariants" class="option info">
                                {{ variantSearchQuery ? "Aucun article trouvé" : "Aucun article disponible" }}
                              </div>
                            </div>
                          </div>
                          <p v-if="errors.cadeauVariantDeclencheur" class="text-danger small mt-2">
                            {{ errors.cadeauVariantDeclencheur }}
                          </p>
                        </div>

                        <!-- Fréquence déclencheur -->
                        <div class="input-form w-100 mt-3">
                          <label class="label-control required">Fréquence déclencheur</label>
                          <select class="form-select round-20" v-model="formData.cadeauFrequence" required>
                            <option value="forfaitaire">Forfaitaire</option>
                            <option value="tous_x_produits">Tous X produits</option>
                          </select>
                          <p class="field-hint">
                            Forfaitaire : À partir de 5 article(s) déclencheur(s) au panier, on gagne 1 article(s). si 5
                            article(s) déclencheur(s) au panier, on gagne 1 article(s), au delà on ne gagne pas
                            d'article supplémentaire.
                          </p>
                          <p v-if="errors.cadeauFrequence" class="text-danger small mt-2">
                            {{ errors.cadeauFrequence }}
                          </p>
                        </div>

                        <!-- Coef multiplicateur (Conditional for proportionnel) -->
                        <div v-if="formData.cadeauFrequence === 'proportionnel'" class="input-form w-100 mt-3">
                          <label class="label-control required">Coef multiplicateur</label>
                          <input
                            type="number"
                            class="form-control round-20"
                            v-model="formData.cadeauCoefMultiplicateur"
                            placeholder="1"
                            min="1"
                            required
                          />
                          <p class="field-hint">Entrez le coefficient multiplicateur pour l'offre proportionnelle</p>
                          <p v-if="errors.cadeauCoefMultiplicateur" class="text-danger small mt-2">
                            {{ errors.cadeauCoefMultiplicateur }}
                          </p>
                        </div>

                        <!-- X (Conditional for tous_x_produits) -->
                        <div v-if="formData.cadeauFrequence === 'tous_x_produits'" class="input-form w-100 mt-3">
                          <label class="label-control required">X</label>
                          <input
                            type="number"
                            class="form-control round-20"
                            v-model="formData.cadeauXProduits"
                            placeholder="1"
                            min="1"
                            required
                          />
                          <p class="field-hint">Entrez le nombre de produits nécessaires pour déclencher l'offre</p>
                          <p v-if="errors.cadeauXProduits" class="text-danger small mt-2">
                            {{ errors.cadeauXProduits }}
                          </p>
                        </div>

                        <!-- Quantité déclencheur -->
                        <div class="input-form w-100 mt-3">
                          <label class="label-control required">Quantité déclencheur</label>
                          <input
                            type="number"
                            class="form-control round-20"
                            v-model="formData.cadeauQuantiteDeclencheur"
                            placeholder="1"
                            min="1"
                            required
                          />
                          <p class="field-hint">Quantité nécessaire pour activer l'offre</p>
                          <p v-if="errors.cadeauQuantiteDeclencheur" class="text-danger small mt-2">
                            {{ errors.cadeauQuantiteDeclencheur }}
                          </p>
                        </div>

                        <!-- Article gagné -->
                        <div class="input-form w-100 mt-3">
                          <label class="label-control required">Article gagné</label>
                          <select class="form-select round-20" v-model="formData.cadeauArticleGagne" required>
                            <option value="identique">Identique</option>
                            <option value="autre">Autres</option>
                          </select>
                          <p class="field-hint">Les articles "gagnés" seront toujours exprimés en U (unités)</p>
                          <p v-if="errors.cadeauArticleGagne" class="text-danger small mt-2">
                            {{ errors.cadeauArticleGagne }}
                          </p>
                        </div>

                        <!-- Variant Gagné with Search -->
                        <div v-if="formData.cadeauArticleGagne === 'autre'" class="input-form w-100 mt-3">
                          <label class="label-control required">Sélection de l'article gagné</label>
                          <div class="custom-select-wrapper">
                            <div
                              class="custom-select round-20"
                              :class="{ disabled: isLoadingVariants }"
                              @click="toggleVariantDropdown('cadeauVariantGagne')"
                            >
                              <span class="selected-option">
                                {{ getSelectedVariantDisplay("cadeauVariantGagne") }}
                              </span>
                              <span class="arrow" :class="{ open: showVariantDropdown.cadeauVariantGagne }"
                                ><img :src="iconDropDown"
                              /></span>
                            </div>
                            <div v-if="showVariantDropdown.cadeauVariantGagne" class="custom-select-options">
                              <div class="search-container">
                                <input
                                  type="text"
                                  v-model="variantSearchTemp"
                                  placeholder="Rechercher un article..."
                                  class="form-control search-input"
                                  @input="debouncedVariantSearch($event.target.value)"
                                  @click.stop
                                />
                              </div>
                              <div
                                class="option"
                                v-for="variant in availableVariants"
                                :key="variant.id"
                                @click="selectVariant(variant.id, 'cadeauVariantGagne')"
                              >
                                {{ variant.name }}
                              </div>
                              <div v-if="isLoadingVariants" class="option loading">Chargement des articles...</div>
                              <div v-if="availableVariants.length === 0 && !isLoadingVariants" class="option info">
                                {{ variantSearchQuery ? "Aucun article trouvé" : "Aucun article disponible" }}
                              </div>
                            </div>
                          </div>
                          <p v-if="errors.cadeauVariantGagne" class="text-danger small mt-2">
                            {{ errors.cadeauVariantGagne }}
                          </p>
                        </div>

                        <!-- Quantité gagnée -->
                        <div class="input-form w-100 mt-3">
                          <label class="label-control required">Quantité gagnée</label>
                          <input
                            type="number"
                            class="form-control round-20"
                            v-model="formData.cadeauQuantiteGagnee"
                            placeholder="1"
                            min="1"
                            required
                          />
                          <p class="field-hint">Nombre d'unités offertes</p>
                          <p v-if="errors.cadeauQuantiteGagnee" class="text-danger small mt-2">
                            {{ errors.cadeauQuantiteGagnee }}
                          </p>
                        </div>

                        <!-- Toggles -->
                        <div class="toggle-group mt-4 col-4">
                          <div class="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <label class="label-control required mb-0">Quantité modifiable ?</label>
                              <p class="field-hint mb-0">quantité du produit cadeau</p>
                            </div>
                            <div class="form-check form-switch">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="formData.cadeauQuantiteModifiable"
                              />
                            </div>
                          </div>

                          <div class="d-flex justify-content-between align-items-center">
                            <div>
                              <label class="label-control required mb-0">Peut être supprimé du panier ?</label>
                            </div>
                            <div class="form-check form-switch">
                              <input
                                class="form-check-input"
                                type="checkbox"
                                v-model="formData.cadeauSupprimablePanier"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Period Settings -->
              <div class="period-section mt-5">
                <div class="section-header">
                  <h5 class="section-title blue-color">Période de validité</h5>
                  <p class="section-subtitle">Définissez la période d'application de l'offre</p>
                </div>

                <div class="row mt-3">
                  <div class="col-md-6">
                    <div class="input-form w-100">
                      <label class="label-control">Date de début</label>
                      <input type="date" class="form-control round-20" v-model="formData.startDate" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="input-form w-100">
                      <label class="label-control">Date de fin</label>
                      <input type="date" class="form-control round-20" v-model="formData.endDate" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Validation Errors -->
              <div v-if="Object.keys(errors).length > 0" class="alert alert-danger mt-4">
                <p class="mb-0">Veuillez corriger les erreurs suivantes :</p>
                <ul class="mb-0">
                  <li v-for="(error, key) in errors" :key="key">{{ error }}</li>
                </ul>
              </div>

              <!-- Submit Button -->
              <div class="d-flex justify-content-center mt-5">
                <button type="submit" class="btn btn-orange w-30" :disabled="isSubmitting">
                  {{
                    isSubmitting
                      ? isEditing
                        ? "Mise à jour..."
                        : "Création..."
                      : isEditing
                        ? "Modifier l'offre"
                        : $t("loyalty_offer.form.validate")
                  }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import plus from "@/assets/images/plus.svg";
  import { ref, reactive, computed, onMounted, watch, onUnmounted } from "vue";
  import { useRegisterStore } from "@/stores/register";
  import { useGammesStore } from "@/stores/gammesStore";
  import { useProductsStore } from "@/stores/productsStore";
  import {
    getClients,
    createPromotion,
    getVeterinairePromotions,
    togglePromotionStatus,
    updatePromotion,
  } from "@/api/offerveto";
  import { fetchProductVariantPriceCoefs, fetchProductByCode, fetchProductById } from "@/api/veterinaireService";
  import { debounce } from "lodash";
  import iconDown from "@/assets/images/icon-row-down.svg";
  import { useToast } from "vue-toastification";

  export default {
    name: "OffreFidelite",

    setup() {
      const showModal = ref(false);
      const registerStore = useRegisterStore();
      const gammesStore = useGammesStore();
      const productsStore = useProductsStore();
      const toast = useToast();

      // Reactive offers data
      const offres = ref([]);
      const isLoadingOffers = ref(false);
      const offersLoadError = ref(false);
      const isSubmitting = ref(false);
      const isEditing = ref(false);
      const currentEditId = ref(null);

      // Reactive clients data
      const clients = ref([]);
      const isLoadingClients = ref(false);
      const clientsLoadError = ref(false);
      const clientsLoaded = ref(false);

      // Reactive variants data
      const variants = ref([]);
      const filteredVariants = ref([]);
      const isLoadingVariants = ref(false);
      const variantsLoadError = ref(false);
      const variantSearchQuery = ref("");
      const variantSearchTemp = ref("");

      const iconDropDown = iconDown;

      // Separate product data for each dropdown
      const produitsData = reactive({
        produits: [],
        isLoading: false,
        loadError: false,
        currentPage: 1,
        hasMore: true,
        searchQuery: "",
      });

      const cadeauProduitsData = reactive({
        produits: [],
        isLoading: false,
        loadError: false,
        currentPage: 1,
        hasMore: true,
        searchQuery: "",
      });

      const cumulProduitsData = reactive({
        produits: [],
        isLoading: false,
        loadError: false,
        currentPage: 1,
        hasMore: true,
        searchQuery: "",
      });

      const actionProduitsData = reactive({
        produits: [],
        isLoading: false,
        loadError: false,
        currentPage: 1,
        hasMore: true,
        searchQuery: "",
      });

      // Search queries for each dropdown
      const searchQueries = ref({
        produit: "",
        cadeauProduitDeclencheur: "",
        cumulProduit: "",
        actionProduit: "",
      });

      // Custom dropdown state
      const showProductDropdown = ref({
        produit: false,
        cadeauProduitDeclencheur: false,
        cumulProduit: false,
        actionProduit: false,
      });

      const showVariantDropdown = ref({
        variant: false,
        cadeauVariantDeclencheur: false,
        cadeauVariantGagne: false,
        cumulVariant: false,
        actionVariant: false,
      });

      // Validation errors
      const errors = reactive({});

      const formData = reactive({
        name: "",
        descriptionOffre: "",
        regles: [],

        gammes: [],
        clients: [],
        sousGammes: [],
        produit: "",
        variant: "",
        action: "",
        montant: "",
        pourcentage: "",
        produitCadeau: "",
        startDate: "",
        endDate: "",
        cadeauDeclencheur: "",
        cadeauTaxon: "",
        cadeauProduitDeclencheur: "",
        cadeauVariantDeclencheur: "",
        cadeauFrequence: "forfaitaire",
        cadeauQuantiteDeclencheur: null,
        cadeauArticleGagne: "identique",
        cadeauVariantGagne: "",
        cadeauQuantiteGagnee: 1,
        cadeauQuantiteModifiable: false,
        cadeauSupprimablePanier: false,
        cadeauCoefMultiplicateur: 1,
        cadeauXProduits: 1,
        // Action filters
        actionGammes: [],
        actionSousGammes: [],
        actionProduit: "",
        actionVariant: "",
        // Cumul Fidelite fields
        cumulNombreDeclencheur: 1,
        cumulType: "quantites", // 'quantites' or 'unique'
        cumulGammes: [],
        cumulSousGammes: [],
        cumulClients: [],
        cumulProduit: "",
        cumulVariant: "",
        displayOffre: true,
      });

      // Computed properties for filtered products
      const filteredProduits = computed(() => {
        return produitsData.produits;
      });

      const filteredCadeauProduits = computed(() => {
        return cadeauProduitsData.produits;
      });

      const filteredCumulProduits = computed(() => {
        return cumulProduitsData.produits;
      });

      const filteredActionProduits = computed(() => {
        return actionProduitsData.produits;
      });

      // Computed property for filtered variants with search
      const availableVariants = computed(() => {
        if (!variantSearchQuery.value) {
          return filteredVariants.value.length > 0 ? filteredVariants.value : variants.value;
        }

        const query = variantSearchQuery.value.toLowerCase();
        return filteredVariants.value.filter(variant => variant.name.toLowerCase().includes(query));
      });

      const availableProduits = computed(() => produitsData.produits);
      const availableCadeauProduits = computed(() => cadeauProduitsData.produits);
      const availableCumulProduits = computed(() => cumulProduitsData.produits);

      const isLoadingProduits = computed(() => produitsData.isLoading);
      const isLoadingCadeauProduits = computed(() => cadeauProduitsData.isLoading);
      const isLoadingCumulProduits = computed(() => cumulProduitsData.isLoading);
      const isLoadingActionProduits = computed(() => actionProduitsData.isLoading);

      const produitsLoadError = computed(() => produitsData.loadError);
      const cadeauProduitsLoadError = computed(() => cadeauProduitsData.loadError);
      const cumulProduitsLoadError = computed(() => cumulProduitsData.loadError);

      const hasMoreProduits = computed(() => produitsData.hasMore);
      const hasMoreCadeauProduits = computed(() => cadeauProduitsData.hasMore);
      const hasMoreCumulProduits = computed(() => cumulProduitsData.hasMore);
      const hasMoreActionProduits = computed(() => actionProduitsData.hasMore);

      // Debounced search functions
      const debouncedSearchProducts = debounce(async type => {
        const data =
          type === "produit"
            ? produitsData
            : type === "cadeauProduitDeclencheur"
              ? cadeauProduitsData
              : type === "cumulProduit"
                ? cumulProduitsData
                : actionProduitsData;
        data.searchQuery = searchQueries.value[type];
        data.currentPage = 1;
        data.produits = [];
        data.hasMore = true;
        await fetchProducts(1, type, false);
      }, 300);

      const debouncedVariantSearch = debounce(query => {
        variantSearchQuery.value = query;
      }, 300);

      let variantsPromise = null;
      const fetchVariants = async () => {
        if (variantsPromise) return variantsPromise;

        variantsPromise = (async () => {
          isLoadingVariants.value = true;
          variantsLoadError.value = false;
          try {
            const data = await fetchProductVariantPriceCoefs();
            variants.value = data.map(variant => ({
              id: variant.productVariantDetails.id,
              code: variant.productVariantDetails.code,
              name: `${variant.productVariantDetails?.inventoryName || ""} ${variant.productVariantDetails?.name || ""}`.trim(),
            }));

            // Initialize filtered variants with all variants
            filteredVariants.value = [...variants.value];
          } catch (error) {
            console.error("Error fetching articles:", error);
            variantsLoadError.value = true;
            variants.value = [];
            filteredVariants.value = [];
          } finally {
            isLoadingVariants.value = false;
            variantsPromise = null;
          }
        })();

        return variantsPromise;
      };

      // Fetch products with search query
      const fetchProducts = async (page = 1, type = "produit", append = false) => {
        const data =
          type === "produit"
            ? produitsData
            : type === "cadeauProduitDeclencheur"
              ? cadeauProduitsData
              : type === "cumulProduit"
                ? cumulProduitsData
                : actionProduitsData;

        if (!data.hasMore || data.isLoading) return;

        data.isLoading = true;
        data.loadError = false;

        try {
          // Using existing productsStore.fetchAllProducts
          const response = await productsStore.fetchAllProducts(
            { page, itemsPerPage: 30, append },
            data.searchQuery,
            "",
            "name"
          );

          if (append) {
            data.produits = [...data.produits, ...response.items];
          } else {
            data.produits = response.items;
          }

          data.hasMore = data.produits.length < response.total;
          data.currentPage = page;
        } catch (error) {
          console.error(`Error fetching products for ${type}:`, error);
          data.loadError = true;
        } finally {
          data.isLoading = false;
        }
      };

      // Handle scroll for pagination
      const handleProductScroll = async (type, event) => {
        const data =
          type === "produit"
            ? produitsData
            : type === "cadeauProduitDeclencheur"
              ? cadeauProduitsData
              : cumulProduitsData;

        if (data.isLoading || !data.hasMore) return;

        const element = event.target;
        const isNearBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 10;

        if (isNearBottom) {
          try {
            await fetchProducts(data.currentPage + 1, type, true);
          } catch (error) {
            console.error(`Error during product scroll fetch for ${type}:`, error);
            data.loadError = true;
          }
        }
      };

      // Toggle product dropdown
      const toggleProductDropdown = async field => {
        const data = getProductData(field);

        if (data.isLoading) return;

        // If opening dropdown, reset and fetch full product list
        if (!showProductDropdown.value[field]) {
          resetProductData(field);
          await fetchProducts(1, field, false);
        }

        showProductDropdown.value[field] = !showProductDropdown.value[field];

        Object.keys(showProductDropdown.value).forEach(key => {
          if (key !== field) showProductDropdown.value[key] = false;
        });

        Object.keys(showVariantDropdown.value).forEach(key => {
          showVariantDropdown.value[key] = false;
        });
      };

      // Variant dropdown management
      const toggleVariantDropdown = field => {
        if (isLoadingVariants.value) return;

        showVariantDropdown.value[field] = !showVariantDropdown.value[field];

        Object.keys(showVariantDropdown.value).forEach(key => {
          if (key !== field) showVariantDropdown.value[key] = false;
        });

        Object.keys(showProductDropdown.value).forEach(key => {
          showProductDropdown.value[key] = false;
        });

        if (showVariantDropdown.value[field]) {
          variantSearchTemp.value = "";
          variantSearchQuery.value = "";
        }
      };

      // Helper function to get product data
      const getProductData = field => {
        return field === "produit"
          ? produitsData
          : field === "cadeauProduitDeclencheur"
            ? cadeauProduitsData
            : field === "cumulProduit"
              ? cumulProduitsData
              : actionProduitsData;
      };

      // Get display text for selected product
      const getSelectedProductDisplay = field => {
        const data = getProductData(field);
        const selectedId = formData[field];

        if (!selectedId) {
          if (data.isLoading) return "Chargement des produits...";
          if (data.loadError) return "Erreur - réessayer";
          if (data.produits.length === 0) return "Sélectionner un produit...";
          return "Sélectionner un produit...";
        }

        const productList =
          field === "produit"
            ? availableProduits.value
            : field === "cadeauProduitDeclencheur"
              ? availableCadeauProduits.value
              : field === "cumulProduit"
                ? availableCumulProduits.value
                : filteredActionProduits.value;
        const product = productList.find(p => p.code == selectedId || p.id == selectedId);
        return product ? product.name : "chargement...";
      };

      const getSelectedProductDeclencheurDisplay = field => {
        const data = getProductData(field);
        const selectedId = formData[field];

        if (!selectedId) {
          if (data.isLoading) return "Chargement des produits...";
          if (data.loadError) return "Erreur - réessayer";
          if (data.produits.length === 0) return "Sélectionner un produit...";
          return "Sélectionner un produit...";
        }

        const productList =
          field === "produit"
            ? availableProduits.value
            : field === "cadeauProduitDeclencheur"
              ? availableCadeauProduits.value
              : field === "cumulProduit"
                ? availableCumulProduits.value
                : filteredActionProduits.value;
        const product = productList.find(p => p.id == selectedId || p.code == selectedId);
        return product ? product.name : "chargement...";
      };

      // Get display text for selected variant
      const getSelectedVariantDisplay = field => {
        const selectedId = formData[field];

        if (!selectedId) {
          if (isLoadingVariants.value) return "Chargement des articles...";
          if (variantsLoadError.value) return "Erreur - réessayer";
          if (availableVariants.value.length === 0) return "Sélectionner un article...";
          return "Sélectionner un article...";
        }

        const variant = availableVariants.value.find(v => v.id == selectedId);
        return variant ? variant.name : "Article sélectionné";
      };

      const getVariantCodeFromId = variantId => {
        const variant = variants.value.find(v => v.id == variantId);
        return variant ? variant.code : null;
      };

      const getVariantIdFromCode = variantCode => {
        const variant = variants.value.find(v => v.code == variantCode);
        return variant ? variant.id : null;
      };

      // Select product
      const selectProduct = (code, field) => {
        formData[field] = code.toString();
        showProductDropdown.value[field] = false;
        searchQueries.value[field] = "";
        const data = getProductData(field);
        data.searchQuery = "";
      };

      // Select variant
      const selectVariant = (id, field) => {
        formData[field] = id.toString();
        showVariantDropdown.value[field] = false;
        variantSearchTemp.value = "";
        variantSearchQuery.value = "";
      };

      // Close dropdowns when clicking outside
      const handleClickOutside = event => {
        const dropdowns = document.querySelectorAll(".custom-select-wrapper");
        let clickedInside = false;

        dropdowns.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInside = true;
          }
        });

        if (!clickedInside) {
          Object.keys(showProductDropdown.value).forEach(key => {
            showProductDropdown.value[key] = false;
          });

          Object.keys(showVariantDropdown.value).forEach(key => {
            showVariantDropdown.value[key] = false;
          });
        }
      };

      // Reset product data
      const resetProductData = (type = "all") => {
        if (type === "all" || type === "produit") {
          Object.assign(produitsData, {
            produits: [],
            isLoading: false,
            loadError: false,
            currentPage: 1,
            hasMore: true,
            searchQuery: "",
          });
          searchQueries.value.produit = "";
        }

        if (type === "all" || type === "cadeauProduitDeclencheur") {
          Object.assign(cadeauProduitsData, {
            produits: [],
            isLoading: false,
            loadError: false,
            currentPage: 1,
            hasMore: true,
            searchQuery: "",
          });
          searchQueries.value.cadeauProduitDeclencheur = "";
        }

        if (type === "all" || type === "cumulProduit") {
          Object.assign(cumulProduitsData, {
            produits: [],
            isLoading: false,
            loadError: false,
            currentPage: 1,
            hasMore: true,
            searchQuery: "",
          });
          searchQueries.value.cumulProduit = "";
        }

        if (type === "all" || type === "actionProduit") {
          Object.assign(actionProduitsData, {
            produits: [],
            isLoading: false,
            loadError: false,
            currentPage: 1,
            hasMore: true,
            searchQuery: "",
          });
          searchQueries.value.actionProduit = "";
        }
      };

      // Computed properties
      const availableTaxons = computed(() => {
        return availableGammes.value.map(gamme => ({
          id: gamme.id,
          name: gamme.name,
        }));
      });

      const availableGammes = computed(() => {
        const hasSubscription = registerStore.VeterinaireData?.hasSubscription;
        if (!hasSubscription || !hasSubscription.GammeEnVente || !gammesStore.gammes.length) {
          return [];
        }
        const allowedGammeCodes = hasSubscription.GammeEnVente.map(url => {
          const match = url.match(/\/taxons\/(.+)$/);
          return match ? match[1] : null;
        }).filter(code => code !== null);
        return gammesStore.gammes.filter(gamme => allowedGammeCodes.includes(gamme.code));
      });

      const availableGammesOptions = computed(() => {
        return availableGammes.value.map(gamme => gamme.name);
      });

      const getGammeIdsFromNames = gammeNames => {
        return gammeNames
          .map(name => {
            const gamme = availableGammes.value.find(g => g.name === name);
            return gamme ? gamme.id : null;
          })
          .filter(id => id !== null);
      };

      const getGammeCodesFromNames = gammeNames => {
        return gammeNames
          .map(name => {
            const gamme = availableGammes.value.find(g => g.name === name);
            return gamme ? gamme.code : null;
          })
          .filter(code => code !== null);
      };

      const getGammeNamesFromIds = gammeIds => {
        return gammeIds
          .map(id => {
            const gamme = availableGammes.value.find(g => g.id === id);
            return gamme ? gamme.name : null;
          })
          .filter(name => name !== null);
      };

      const getGammeNamesFromCodes = gammeCodes => {
        return gammeCodes
          .map(code => {
            const gamme = availableGammes.value.find(g => g.code === code);
            return gamme ? gamme.name : null;
          })
          .filter(name => name !== null);
      };

      const availableSousGammes = computed(() => {
        const sousGammes = [];
        availableGammes.value.forEach(gamme => {
          if (gamme.childrenDetails && gamme.childrenDetails.length > 0) {
            gamme.childrenDetails.forEach(sousGamme => {
              sousGammes.push({
                id: sousGamme.id,
                name: sousGamme.name,
                code: sousGamme.code,
                gammeName: gamme.name,
                gammeId: gamme.id,
              });
            });
          }
        });
        return sousGammes;
      });

      const availableSousGammesOptions = computed(() => {
        return availableSousGammes.value.map(sousGamme => sousGamme.name);
      });

      const getSousGammeIdsFromNames = sousGammeNames => {
        return sousGammeNames
          .map(name => {
            const sousGamme = availableSousGammes.value.find(sg => sg.name === name);
            return sousGamme ? sousGamme.id : null;
          })
          .filter(id => id !== null);
      };

      const getSousGammeCodesFromNames = sousGammeNames => {
        return sousGammeNames
          .map(name => {
            const sousGamme = availableSousGammes.value.find(sg => sg.name === name);
            return sousGamme ? sousGamme.code : null;
          })
          .filter(code => code !== null);
      };

      const getSousGammeNamesFromIds = sousGammeIds => {
        return sousGammeIds
          .map(id => {
            const sousGamme = availableSousGammes.value.find(sg => sg.id === id);
            return sousGamme ? sousGamme.name : null;
          })
          .filter(name => name !== null);
      };

      const getSousGammeNamesFromCodes = sousGammeCodes => {
        return sousGammeCodes
          .map(code => {
            const sousGamme = availableSousGammes.value.find(sg => sg.code === code);
            return sousGamme ? sousGamme.name : null;
          })
          .filter(name => name !== null);
      };


      const availableRules = [
        { value: "clients", label: "Clients" },
        { value: "notion_cumul", label: "Cumul fidélité" },
      ];

      const isPercentageSelect = computed(() => {
        const hasNotionCumul = formData.regles.includes("notion_cumul");
        const hasClients = formData.regles.includes("clients");
        const hasActionFilters =
          formData.actionGammes.length > 0 ||
          formData.actionSousGammes.length > 0 ||
          formData.actionProduit ||
          formData.actionVariant;

        return hasNotionCumul || (hasNotionCumul && !hasClients && !hasActionFilters);
      });

      const availableClients = computed(() => {
        return clients.value;
      });

      const getClientDisplayName = client => {
        if (typeof client === "string" || typeof client === "number") {
          const clientObj = clients.value.find(c => c.id == client);
          return clientObj ? formatClientName(clientObj) : `Client ${client}`;
        }
        return formatClientName(client);
      };

      const formatClientName = client => {
        if (!client) return "Client inconnu";
        if (client.companyName) return client.companyName;
        if (client.firstName && client.lastName) return `${client.firstName} ${client.lastName}`;
        if (client.email) return client.email;
        if (client.name) return client.name;
        return `Client ${client.id}`;
      };

      const getClientsDropdownText = () => {
        if (isLoadingClients.value) return "Chargement des clients...";
        if (clientsLoadError.value) return "Erreur de chargement - cliquez pour réessayer";
        if (!clientsLoaded.value) return "Sélectionner un client...";
        if (clients.value.length === 0) return "Aucun client disponible";
        return "Sélectionner un client...";
      };

      const openModal = () => {
        isEditing.value = false;
        currentEditId.value = null;
        showModal.value = true;
        resetErrors();
      };

      const closeModal = () => {
        showModal.value = false;
        resetForm();
        resetErrors();
      };

      const resetForm = () => {
        Object.assign(formData, {
          name: "",
          descriptionOffre: "",
          regles: [],

          gammes: [],
          clients: [],
          sousGammes: [],
          produit: "",
          variant: "",
          action: "",
          montant: "",
          pourcentage: "",
          produitCadeau: "",
          startDate: "",
          endDate: "",
          cadeauDeclencheur: "",
          cadeauTaxon: "",
          cadeauProduitDeclencheur: "",
          cadeauVariantDeclencheur: "",
          cadeauFrequence: "forfaitaire",
          cadeauQuantiteDeclencheur: null,
          cadeauArticleGagne: "identique",
          cadeauVariantGagne: "",
          cadeauQuantiteGagnee: 1,
          cadeauQuantiteModifiable: false,
          cadeauSupprimablePanier: false,
          cadeauCoefMultiplicateur: 1,
          cadeauXProduits: 1,
          // Reset Cumul Fidelite fields
          cumulNombreDeclencheur: 1,
          cumulType: "quantites",
          cumulGammes: [],
          cumulSousGammes: [],
          cumulClients: [],
          cumulProduit: "",
          cumulVariant: "",
          actionGammes: [],
          actionSousGammes: [],
          actionProduit: "",
          actionVariant: "",
          displayOffre: true,
        });
        resetProductData("all");
        variantSearchTemp.value = "";
        variantSearchQuery.value = "";
        isSubmitting.value = false;
        isEditing.value = false;
        currentEditId.value = null;
        resetErrors();
      };

      const resetErrors = () => {
        Object.keys(errors).forEach(key => delete errors[key]);
      };

      const addRegle = value => {
        if (value && !formData.regles.includes(value)) {
          formData.regles.push(value);
        }
        event.target.value = "";
      };

      const removeRegle = index => {
        const regle = formData.regles[index];
        formData.regles.splice(index, 1);
        if (regle === "clients") {
          formData.clients = [];
          delete errors.clients;
        }
        if (regle === "notion_cumul") {
          formData.cumulNombreDeclencheur = 1;
          formData.cumulType = "quantites";
          formData.cumulGammes = [];
          formData.cumulSousGammes = [];
          formData.cumulClients = [];
          formData.cumulProduit = "";
          formData.cumulVariant = "";
          delete errors.cumulGammes;
          delete errors.cumulSousGammes;
          delete errors.cumulProduit;
          delete errors.cumulVariant;
          delete errors.cumulNombreDeclencheur;
        }
        delete errors.regles;
      };

      const addGamme = value => {
        if (value && !formData.gammes.includes(value)) {
          formData.gammes.push(value);
        }
        event.target.value = "";
        delete errors.gammes;
      };

      const removeGamme = index => {
        formData.gammes.splice(index, 1);
        if (formData.gammes.length === 0 && formData.regles.includes("gamme")) {
          errors.gammes = "Veuillez sélectionner au moins une espèce";
        }
      };

      // Cumul Gamme Helpers
      const addCumulGamme = value => {
        if (value && !formData.cumulGammes.includes(value)) {
          formData.cumulGammes.push(value);
        }
        if (event && event.target) event.target.value = "";
        delete errors.cumulGammes;
      };

      const removeCumulGamme = index => {
        formData.cumulGammes.splice(index, 1);
      };

      // Cumul SousGamme Helpers
      const addCumulSousGamme = value => {
        if (value && !formData.cumulSousGammes.includes(value)) {
          formData.cumulSousGammes.push(value);
        }
        if (event && event.target) event.target.value = "";
        delete errors.cumulSousGammes;
      };

      const removeCumulSousGamme = index => {
        formData.cumulSousGammes.splice(index, 1);
      };

      // Action Filter Helpers
      const addActionGamme = value => {
        if (value && !formData.actionGammes.includes(value)) {
          formData.actionGammes.push(value);
        }
        if (event && event.target) event.target.value = "";
      };

      const removeActionGamme = index => {
        formData.actionGammes.splice(index, 1);
      };

      const addActionSousGamme = value => {
        if (value && !formData.actionSousGammes.includes(value)) {
          formData.actionSousGammes.push(value);
        }
        if (event && event.target) event.target.value = "";
      };

      const removeActionSousGamme = index => {
        formData.actionSousGammes.splice(index, 1);
      };

      const removeActionProduit = () => {
        formData.actionProduit = "";
      };

      const removeActionVariant = () => {
        formData.actionVariant = "";
      };

      const addClient = value => {
        if (value && !formData.clients.includes(value)) {
          formData.clients.push(value);
        }
        event.target.value = "";
        delete errors.clients;
      };

      const removeClient = index => {
        formData.clients.splice(index, 1);
        if (formData.clients.length === 0 && formData.regles.includes("clients")) {
          errors.clients = "Veuillez sélectionner au moins un client";
        }
      };

      const addSousGamme = value => {
        if (value && !formData.sousGammes.includes(value)) {
          formData.sousGammes.push(value);
        }
        if (event && event.target) event.target.value = "";
        delete errors.sousGammes;
      };

      const addCumulClient = id => {
        if (!id) return;
        if (!formData.cumulClients.includes(id)) {
          formData.cumulClients.push(id.toString());
        }
      };

      const removeCumulClient = index => {
        formData.cumulClients.splice(index, 1);
      };

      const removeSousGamme = index => {
        formData.sousGammes.splice(index, 1);
        if (formData.sousGammes.length === 0 && formData.regles.includes("sous-gamme")) {
          errors.sousGammes = "Veuillez sélectionner au moins une gamme";
        }
      };

      const getRuleLabel = value => {
        const rule = availableRules.find(r => r.value === value);
        return rule ? rule.label : value;
      };

      const fetchOffers = async () => {
        isLoadingOffers.value = true;
        offersLoadError.value = false;
        try {
          const response = await getVeterinairePromotions(1, 50);
          offres.value = response.promotions.map(promotion => ({
            id: promotion.id,
            nom: promotion.name,
            produit: extractProductsFromRules(promotion.rules),
            client: extractClientsFromRules(promotion.rules),
            periode: formatPeriod(promotion.startsAt, promotion.endsAt),
            promo: extractPromoType(promotion.actions),
            valeur: extractPromoValue(promotion.actions),
            rawData: promotion,
            active: promotion.enabled,
          }));
        } catch (error) {
          console.error("Error fetching offers:", error);
          offersLoadError.value = true;
          offres.value = [];
        } finally {
          isLoadingOffers.value = false;
        }
      };

      const extractProductsFromRules = rules => {
        if (!rules || typeof rules !== "object") return "Tous produits";
        if (rules.gamme && rules.gamme.gammes && rules.gamme.gammes.length > 0) {
          return `${rules.gamme.gammes.length} gamme(s)`;
        }
        if (rules.sousgamme && rules.sousgamme.sousgammes && rules.sousgamme.sousgammes.length > 0) {
          return `${rules.sousgamme.sousgammes.length} sous-gamme(s)`;
        }
        if (rules.contains_product && rules.contains_product.product_code) {
          return "Produit spécifique";
        }
        if (rules.product_variant && rules.product_variant.productVariant) {
          return "Article spécifique";
        }
        return "Tous produits";
      };

      const extractClientsFromRules = rules => {
        if (!rules || typeof rules !== "object") return "Tous les clients";
        if (rules.customer && rules.customer.customers && rules.customer.customers.length > 0) {
          return `${rules.customer.customers.length} client(s) spécifique(s)`;
        }
        return "Tous les clients";
      };

      const formatPeriod = (startsAt, endsAt) => {
        if (!startsAt || !endsAt) return "Non définie";
        try {
          const startDate = new Date(startsAt);
          const endDate = new Date(endsAt);
          const formatDate = date =>
            `${date.getDate().toString().padStart(2, "0")}/${(date.getMonth() + 1)
              .toString()
              .padStart(2, "0")}/${date.getFullYear()}`;
          return `${formatDate(startDate)} → ${formatDate(endDate)}`;
        } catch (error) {
          return "Période invalide";
        }
      };

      const extractPromoType = actions => {
        if (!actions || !Array.isArray(actions) || actions.length === 0) return "Autre";
        const action = actions[0];
        const typeMap = {
          unit_percentage_discount: "Remise en %",
          unit_fixed_discount: "Remise fixe",
          parametres_declancheur: "Produit cadeau",
        };
        return typeMap[action.type] || action.type;
      };

      const extractPromoValue = actions => {
        if (!actions || !Array.isArray(actions) || actions.length === 0) return "N/A";
        const action = actions[0];
        const config = action.configuration;
        if (!config) return "N/A";
        if (action.type === "unit_percentage_discount") {
          const percentage = config.esupplyvet?.percentage ?? config.supplyvet?.percentage;
          if (percentage !== undefined && percentage !== null) {
            return `${percentage * 100}%`;
          }
        }
        if (action.type === "unit_fixed_discount") {
          const amount = config.esupplyvet?.amount ?? config.supplyvet?.amount;
          if (amount !== undefined && amount !== null) {
            return `${amount / 100}€`;
          }
        }
        if (action.type === "parametres_declancheur") {
          return "Produit offert";
        }
        return "N/A";
      };

      const toggleOfferStatus = async offre => {
        try {
          await togglePromotionStatus(offre.id, offre.active);
          await fetchOffers();
          toast.success(`Offre ${offre.active ? "activée" : "désactivée"} avec succès`, {
            timeout: 3000,
            closeOnClick: true,
            pauseOnFocusLoss: true,
            pauseOnHover: true,
          });
        } catch (error) {
          console.error("Error toggling promotion status:", error);
          offre.active = !offre.active;
          errors.toggleStatus = "Erreur lors de la modification du statut";
        }
      };

      const fetchClients = async () => {
        isLoadingClients.value = true;
        clientsLoadError.value = false;
        try {
          const response = await getClients({
            page: 1,
            itemsPerPage: 100,
          });
          clients.value = response.clients || [];
          clientsLoaded.value = true;
        } catch (error) {
          console.error("Error fetching clients:", error);
          clientsLoadError.value = true;
          clients.value = [];
        } finally {
          isLoadingClients.value = false;
        }
      };

      const validateForm = () => {
        resetErrors();

        let isValid = true;

        if (!formData.name) {
          errors.name = "Veuillez saisir un nom pour l'offre";
          isValid = false;
        }
        if (formData.regles.includes("clients") && !formData.clients.length) {
          errors.clients = "Veuillez sélectionner au moins un client";
          isValid = false;
        }
        if (formData.regles.includes("notion_cumul")) {
          if (!formData.cumulNombreDeclencheur) {
            errors.cumulNombreDeclencheur = "Veuillez spécifier le nombre déclencheur";
            isValid = false;
          }
        }

        if (formData.action === "cadeau") {
          const isCumul = formData.regles.includes("notion_cumul");

          if (!isCumul) {
            if (!formData.cadeauDeclencheur) {
              errors.cadeauDeclencheur = "Veuillez sélectionner un déclencheur pour l'offre cadeau";
              isValid = false;
            }
            if (formData.cadeauDeclencheur === "taxon" && !formData.cadeauTaxon) {
              errors.cadeauTaxon = "Veuillez sélectionner une espèce";
              isValid = false;
            }
            if (formData.cadeauDeclencheur === "product" && !formData.cadeauProduitDeclencheur) {
              errors.cadeauProduitDeclencheur = "Veuillez sélectionner un produit déclencheur";
              isValid = false;
            }
            if (formData.cadeauDeclencheur === "variants" && !formData.cadeauVariantDeclencheur) {
              errors.cadeauVariantDeclencheur = "Veuillez sélectionner un article déclencheur";
              isValid = false;
            }
            if (!formData.cadeauFrequence) {
              errors.cadeauFrequence = "Veuillez sélectionner une fréquence déclencheur";
              isValid = false;
            }
            if (formData.cadeauFrequence === "proportionnel" && !formData.cadeauCoefMultiplicateur) {
              errors.cadeauCoefMultiplicateur = "Veuillez spécifier un coefficient multiplicateur";
              isValid = false;
            }
            if (formData.cadeauFrequence === "tous_x_produits" && !formData.cadeauXProduits) {
              errors.cadeauXProduits = "Veuillez spécifier le nombre de produits";
              isValid = false;
            }
            if (!formData.cadeauQuantiteDeclencheur) {
              errors.cadeauQuantiteDeclencheur = "Veuillez spécifier une quantité déclencheur";
              isValid = false;
            }
            if (!formData.cadeauArticleGagne) {
              errors.cadeauArticleGagne = "Veuillez sélectionner un article gagné";
              isValid = false;
            }
            if (formData.cadeauArticleGagne === "autre" && !formData.cadeauVariantGagne) {
              errors.cadeauVariantGagne = "Veuillez sélectionner un article gagné";
              isValid = false;
            }
          } else {
            // Validation specific to Cumul + Cadeau
            if (!formData.cadeauVariantGagne) {
              errors.cadeauVariantGagne = "Veuillez sélectionner un article gagné";
              isValid = false;
            }
          }

          if (!formData.cadeauQuantiteGagnee) {
            errors.cadeauQuantiteGagnee = "Veuillez spécifier une quantité gagnée";
            isValid = false;
          }
        }

        if (formData.action === "pourcentage" && !formData.pourcentage) {
          errors.pourcentage = "Veuillez spécifier un pourcentage de réduction";
          isValid = false;
        }

        return isValid;
      };

      const handleSubmit = async () => {
        if (!validateForm()) {
          return;
        }

        isSubmitting.value = true;

        try {
          const promotionPayload = buildPromotionPayload();
          if (isEditing.value && currentEditId.value) {
            const response = await updatePromotion(currentEditId.value, promotionPayload);
            console.log("Promotion updated successfully:", response);
            toast.success("Offre modifiée avec succès", {
              timeout: 3000,
              closeOnClick: true,
              pauseOnFocusLoss: true,
              pauseOnHover: true,
            });
          } else {
            const response = await createPromotion(promotionPayload);
            console.log("Promotion created successfully:", response);
            toast.success("Offre créée avec succès", {
              timeout: 3000,
              closeOnClick: true,
              pauseOnFocusLoss: true,
              pauseOnHover: true,
            });
          }
          await fetchOffers();
          closeModal();
          errors.success = `Offre ${isEditing.value ? "modifiée" : "créée"} avec succès!`;
        } catch (error) {
          console.error("Error saving promotion:", error);
          errors.submit = `Erreur lors de ${isEditing.value ? "la modification" : "la création"} de l'offre. Veuillez réessayer.`;
        } finally {
          isSubmitting.value = false;
        }
      };

      const buildPromotionPayload = () => {
        const payload = {
          name: formData.name,
          descriptionOffre: formData.descriptionOffre,
          startsAt: formData.startDate ? `${formData.startDate} 00:00` : null,

          endsAt: formData.endDate ? `${formData.endDate} 23:59` : null,
          rules: {},
          actions: {},
          displayOffre: formData.displayOffre,
        };

        if (formData.regles.includes("clients") && formData.clients.length > 0) {
          payload.rules.customer = {
            customers: formData.clients.map(id => parseInt(id)),
          };
        }


        if (formData.regles.includes("notion_cumul")) {
          const cumulPayload = {
            trigger_quantity: parseInt(formData.cumulNombreDeclencheur),
            trigger_type: formData.cumulType === "quantites" ? "variant" : "product",
            customers: [],
            gammes: [],
            sousgammes: [],
            products: [],
            productVariants: [],
          };

          if (formData.cumulClients.length > 0) {
            cumulPayload.customers = formData.cumulClients.map(id => parseInt(id));
          }
          if (formData.cumulGammes.length > 0) {
            cumulPayload.gammes = getGammeIdsFromNames(formData.cumulGammes);
          }
          if (formData.cumulSousGammes.length > 0) {
            cumulPayload.sousgammes = getSousGammeIdsFromNames(formData.cumulSousGammes);
          }
          if (formData.cumulProduit) {
            cumulPayload.products = [parseInt(formData.cumulProduit)];
          }
          if (formData.cumulVariant) {
            cumulPayload.productVariants = [parseInt(formData.cumulVariant)];
          }

          payload.rules.notion_cumul = cumulPayload;
        }

        const emptyFilters = {
          price_range_filter: { min: null, max: null },
          taxons_filter: { taxons: [] },
          products_filter: { products: [] },
          variants_filter: { variants: [] },
        };

        const buildActionFilters = () => {
          if (formData.regles.includes("notion_cumul")) {
            return emptyFilters;
          }

          const taxons = [
            ...getGammeCodesFromNames(formData.actionGammes),
            ...getSousGammeCodesFromNames(formData.actionSousGammes),
          ];
          const products = formData.actionProduit ? [formData.actionProduit] : [];
          const variantCode = formData.actionVariant ? getVariantCodeFromId(formData.actionVariant) : null;
          const variants = variantCode ? [variantCode] : [];

          return {
            price_range_filter: { min: null, max: null },
            taxons_filter: { taxons },
            products_filter: { products },
            variants_filter: { variants },
          };
        };

        if (formData.action === "pourcentage" && formData.pourcentage) {
          const percentage = parseFloat(formData.pourcentage) / 100;
          const filters = buildActionFilters();
          payload.actions.unit_percentage_discount = {
            certivet: {
              filters: emptyFilters,
              percentage: null,
            },
            supplyvet: {
              filters: emptyFilters,
              percentage: null,
            },
            esupplyvet: {
              filters: filters,
              percentage: percentage,
            },
          };
        } else if (formData.action === "montant" && formData.montant) {
          const amount = parseFloat(formData.montant) * 100;
          const filters = buildActionFilters();
          payload.actions.unit_fixed_discount = {
            certivet: {
              amount: null,
              filters: emptyFilters,
            },
            supplyvet: {
              amount: null,
              filters: emptyFilters,
            },
            esupplyvet: {
              amount: amount,
              filters: filters,
            },
          };
        } else if (formData.action === "cadeau") {
          payload.actions.parametres_declancheur = {
            declencheur: formData.regles.includes("notion_cumul") ? "all" : formData.cadeauDeclencheur,
            article_gagne: formData.cadeauArticleGagne,
            section_textes: null,
            quantite_gagnee: parseInt(formData.cadeauQuantiteGagnee),
            quantite_modifiable: formData.cadeauQuantiteModifiable,
            cart_trigger_message: null,
            quantite_declencheur: parseInt(formData.cadeauQuantiteDeclencheur),
            section_textes_email: null,
            cart_item_won_message: null,
            frequence_declencheur: formData.cadeauFrequence,
            emails_trigger_message: null,
            emails_item_won_message: null,
            cart_message_same_as_email: false,
            peut_etre_supprimer_panier: formData.cadeauSupprimablePanier,
            just_for_admin_help_display: null,
            show_message_on_product_ticket: false,
            article_gagne_variant:
              formData.cadeauArticleGagne === "autre" && formData.cadeauVariantGagne
                ? parseInt(formData.cadeauVariantGagne)
                : null,
            taxon: formData.cadeauTaxon || null,
            product: formData.cadeauProduitDeclencheur ? parseInt(formData.cadeauProduitDeclencheur) : null,
            variants: formData.cadeauVariantDeclencheur ? [parseInt(formData.cadeauVariantDeclencheur)] : null,
            coef_multiplicateur:
              formData.cadeauFrequence === "proportionnel" ? parseInt(formData.cadeauCoefMultiplicateur) : null,
            tous_x_produits: formData.cadeauFrequence === "tous_x_produits" ? parseInt(formData.cadeauXProduits) : null,
          };
        }

        return payload;
      };

      const editOffer = async offre => {
        resetForm();
        isEditing.value = true;
        currentEditId.value = offre.id;

        const promotion = offre.rawData;

        // Basic form data
        formData.name = promotion.name;
        formData.descriptionOffre = promotion.descriptionOffre || "";
        formData.displayOffre = promotion.displayOffre !== undefined ? promotion.displayOffre : true;


        // Date handling
        if (promotion.startsAt) {
          formData.startDate = promotion.startsAt.split(" ")[0];
        }
        if (promotion.endsAt && promotion.endsAt !== "2026-12-31 00:00:00") {
          formData.endDate = promotion.endsAt.split(" ")[0];
        } else {
          formData.endDate = "";
        }

        // Reset rules first
        formData.regles = [];
        formData.clients = [];
        formData.gammes = [];
        formData.sousGammes = [];
        formData.produit = "";
        formData.variant = "";

        // Parse rules from API response
        if (promotion.rules) {
          const rules = Array.isArray(promotion.rules) ? promotion.rules : [promotion.rules];
          rules.forEach(rule => {
            if (rule.type === "customer" && rule.configuration?.customers) {
              if (!formData.regles.includes("clients")) {
                formData.regles.push("clients");
              }
              formData.clients = rule.configuration.customers.map(id => id.toString());
            } else if (rule.type === "notion_cumul" && rule.configuration) {
              if (!formData.regles.includes("notion_cumul")) {
                formData.regles.push("notion_cumul");
              }
              const config = rule.configuration;
              formData.cumulNombreDeclencheur = config.trigger_quantity;
              formData.cumulType = config.trigger_type === "variant" ? "quantites" : "unique";

              if (config.customers) {
                formData.cumulClients = config.customers.map(id => id.toString());
              }
              if (config.gammes) {
                formData.cumulGammes = getGammeNamesFromIds(config.gammes);
              }
              if (config.sousgammes) {
                formData.cumulSousGammes = getSousGammeNamesFromIds(config.sousgammes);
              }
              if (config.products && config.products.length > 0) {
                formData.cumulProduit = config.products[0].toString();
              }
              if (config.productVariants && config.productVariants.length > 0) {
                formData.cumulVariant = config.productVariants[0].toString();
              }
            }
          });
        }

        let actionVariantCode = null;
        let actionTaxonCodes = [];

        // Parse actions from API response
        if (promotion.actions && Array.isArray(promotion.actions) && promotion.actions.length > 0) {
          const action = promotion.actions[0];
          if (action.type === "unit_percentage_discount") {
            formData.action = "pourcentage";
            const config =
              action.configuration?.esupplyvet || action.configuration?.supplyvet || action.configuration?.certivet;
            if (config) {
              if (config.percentage !== undefined && config.percentage !== null) {
                formData.pourcentage = (config.percentage * 100).toString();
              }
              if (config.filters) {
                const filters = config.filters;
                if (filters.taxons_filter?.taxons) {
                  actionTaxonCodes = filters.taxons_filter.taxons;
                }
                if (filters.products_filter?.products && filters.products_filter.products.length > 0) {
                  formData.actionProduit = filters.products_filter.products[0].toString();
                }
                if (filters.variants_filter?.variants && filters.variants_filter.variants.length > 0) {
                  actionVariantCode = filters.variants_filter.variants[0];
                }
              }
            }
          } else if (action.type === "unit_fixed_discount") {
            formData.action = "montant";
            const config =
              action.configuration?.esupplyvet || action.configuration?.supplyvet || action.configuration?.certivet;
            if (config) {
              if (config.amount !== undefined && config.amount !== null) {
                formData.montant = (config.amount / 100).toString();
              }
              if (config.filters) {
                const filters = config.filters;
                if (filters.taxons_filter?.taxons) {
                  actionTaxonCodes = filters.taxons_filter.taxons;
                }
                if (filters.products_filter?.products && filters.products_filter.products.length > 0) {
                  formData.actionProduit = filters.products_filter.products[0].toString();
                }
                if (filters.variants_filter?.variants && filters.variants_filter.variants.length > 0) {
                  actionVariantCode = filters.variants_filter.variants[0];
                }
              }
            }
          } else if (action.type === "parametres_declancheur") {
            formData.action = "cadeau";
            const config = action.configuration;
            formData.cadeauDeclencheur = config.declencheur || "";
            formData.cadeauTaxon = config.taxon?.toString() || "";
            formData.cadeauProduitDeclencheur = config.product?.toString() || "";
            formData.cadeauVariantDeclencheur =
              config.variants?.[0]?.toString() || config.variant_declencheur?.toString() || "";
            formData.cadeauFrequence = config.frequence_declencheur || "forfaitaire";
            formData.cadeauQuantiteDeclencheur = config.quantite_declencheur || null;
            formData.cadeauArticleGagne = config.article_gagne || "identique";
            formData.cadeauVariantGagne = config.article_gagne_variant?.toString() || "";
            formData.cadeauQuantiteGagnee = config.quantite_gagnee || 1;
            formData.cadeauQuantiteModifiable = config.quantite_modifiable || false;
            formData.cadeauSupprimablePanier = config.peut_etre_supprimer_panier || false;
            formData.cadeauCoefMultiplicateur = config.coef_multiplicateur || 1;
            formData.cadeauXProduits = config.tous_x_produits || 1;
          }
        }

        // Load essential data
        const loadPromises = [];

        // Load clients if needed
        if (
          (formData.regles.includes("clients") || formData.regles.includes("notion_cumul")) &&
          !clientsLoaded.value &&
          !isLoadingClients.value
        ) {
          loadPromises.push(fetchClients());
        }

        // Load variants if needed for cadeau action or action filters
        if (formData.action === "cadeau" || formData.actionVariant || actionVariantCode) {
          loadPromises.push(fetchVariants());
        }

        // Load gammes if needed for action filters
        if (actionTaxonCodes.length > 0 && gammesStore.gammes.length === 0) {
          loadPromises.push(gammesStore.fetchGammes());
        }

        if (formData.regles.includes("produit") && formData.produit) {
          loadPromises.push(
            fetchProductByCode(formData.produit)
              .then(product => {
                produitsData.produits = [
                  {
                    code: product.code,
                    id: product.id,
                    name: product.name,
                  },
                ];
                produitsData.hasMore = false;
              })
              .catch(error => {
                console.error("Failed to fetch product for produit:", error);
                produitsData.loadError = true;
              })
          );
        }

        if (formData.action === "cadeau" && formData.cadeauProduitDeclencheur) {
          loadPromises.push(
            fetchProductById(formData.cadeauProduitDeclencheur)
              .then(product => {
                cadeauProduitsData.produits = [
                  {
                    id: product.id,
                    code: product.code,
                    name: product.name,
                  },
                ];
                cadeauProduitsData.hasMore = false;
              })
              .catch(error => {
                console.error("Failed to fetch product for cadeauProduitDeclencheur:", error);
              })
          );
        }
        if (formData.regles.includes("notion_cumul") && formData.cumulProduit) {
          loadPromises.push(
            fetchProductById(formData.cumulProduit)
              .then(product => {
                cumulProduitsData.produits = [
                  {
                    id: product.id,
                    code: product.code,
                    name: product.name,
                  },
                ];
                cumulProduitsData.hasMore = false;
              })
              .catch(e => console.error("Error loading cumul product", e))
          );
        }

        if ((formData.action === "pourcentage" || formData.action === "montant") && formData.actionProduit) {
          loadPromises.push(
            fetchProductByCode(formData.actionProduit)
              .then(product => {
                actionProduitsData.produits = [
                  {
                    code: product.code,
                    id: product.id,
                    name: product.name,
                  },
                ];
                actionProduitsData.hasMore = false;
              })
              .catch(error => {
                console.error("Failed to fetch product for actionProduit:", error);
                actionProduitsData.loadError = true;
              })
          );
        }

        // Wait for all data to load before opening modal
        try {
          await Promise.all(loadPromises);

          // Resolve codes after loading
          if (actionTaxonCodes.length > 0) {
            formData.actionGammes = getGammeNamesFromCodes(actionTaxonCodes);
            formData.actionSousGammes = getSousGammeNamesFromCodes(actionTaxonCodes);
          }
          if (actionVariantCode) {
            const variantId = getVariantIdFromCode(actionVariantCode);
            formData.actionVariant = variantId ? variantId.toString() : "";
          }
        } catch (error) {
          console.error("Error loading data for edit:", error);
        }

        showModal.value = true;
      };

      // Fetch data on component mount
      onMounted(async () => {
        await fetchOffers();
        try {
          await gammesStore.fetchGammes();
          await fetchVariants();
        } catch (error) {
          console.error("Error fetching initial data:", error);
        }
        document.addEventListener("click", handleClickOutside);
      });

      // Clean up on unmount
      onUnmounted(() => {
        document.removeEventListener("click", handleClickOutside);
      });

      // Watch for clients rule or notion_cumul
      watch(
        () => formData.regles.includes("clients") || formData.regles.includes("notion_cumul"),
        shouldLoadClients => {
          if (shouldLoadClients && !clientsLoaded.value && !isLoadingClients.value) {
            fetchClients();
          }
        }
      );

      // Watch for action changes
      watch(
        () => formData.action,
        newAction => {
          if (newAction === "cadeau" && variants.value.length === 0 && !isLoadingVariants.value) {
            fetchVariants();
          }
        }
      );

      return {
        plus,
        offres,
        showModal,
        formData,
        errors,
        isLoadingOffers,
        offersLoadError,
        isSubmitting,
        isEditing,
        availableRules,
        availableClients,
        availableGammesOptions,
        availableSousGammesOptions,
        isLoadingClients,
        clientsLoadError,
        isLoadingVariants,
        variantsLoadError,
        availableProduits,
        availableCadeauProduits,
        isLoadingProduits,
        isLoadingCadeauProduits,
        produitsLoadError,
        cadeauProduitsLoadError,
        hasMoreProduits,
        hasMoreCadeauProduits,
        filteredProduits,
        filteredCadeauProduits,
        availableCumulProduits,
        isLoadingCumulProduits,
        cumulProduitsLoadError,
        hasMoreCumulProduits,
        filteredCumulProduits,
        isLoadingActionProduits,
        hasMoreActionProduits,
        filteredActionProduits,

        searchQueries,
        variantSearchTemp,
        availableVariants,
        showProductDropdown,
        showVariantDropdown,
        getClientDisplayName,
        getClientsDropdownText,
        getSelectedProductDisplay,
        getSelectedProductDeclencheurDisplay,
        getSelectedVariantDisplay,
        openModal,
        closeModal,
        addRegle,
        removeRegle,
        addGamme,
        removeGamme,
        addClient,
        removeClient,
        addSousGamme,
        removeSousGamme,
        getRuleLabel,
        handleSubmit,
        editOffer,
        fetchClients,
        fetchOffers,
        toggleOfferStatus,
        availableTaxons,
        handleProductScroll,
        fetchProducts,
        toggleProductDropdown,
        toggleVariantDropdown,
        selectProduct,
        selectVariant,
        debouncedSearchProducts,
        debouncedVariantSearch,
        iconDropDown,
        addCumulClient,
        removeCumulClient,
        addCumulGamme,
        removeCumulGamme,
        addCumulSousGamme,
        removeCumulSousGamme,
        addActionGamme,
        removeActionGamme,
        addActionSousGamme,
        removeActionSousGamme,
        removeActionProduit,
        removeActionVariant,
        isPercentageSelect,
      };
    },
  };
</script>
<style scoped>
  .modal.show {
    background-color: rgba(0, 0, 0, 0.5);
  }

  .content-dashboard {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .btn-orange {
    background-color: #ec621a;
    color: white;
    border-radius: 20px;
    padding: 10px 20px;
    font-weight: 600;
  }

  .btn-orange:hover {
    background-color: #d45517;
  }

  /* Section Styling */
  .section-header {
    margin-bottom: 1.5rem;
    border-bottom: 2px solid #f0f4ff;
    padding-bottom: 0.5rem;
  }

  .section-title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .section-subtitle {
    color: #6c757d;
    font-size: 0.875rem;
    margin-bottom: 0;
  }

  /* Rule and Action Sections */
  .rules-section,
  .actions-section,
  .settings-section {
    background: #f8f9fa;
    border-radius: 20px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
  }

  .rule-field-group,
  .action-field-group {
    background: white;
    border-radius: 15px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid #e9ecef;
  }

  .rule-field-group:last-child,
  .action-field-group:last-child {
    margin-bottom: 0;
  }

  /* Field Hint Styling */
  .field-hint {
    font-size: 0.75rem;
    color: #6c757d;
    margin-top: 0.5rem;
    margin-bottom: 0;
    font-style: italic;
  }

  /* Input with Symbol */
  .input-with-symbol {
    position: relative;
  }

  .input-symbol {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    color: #6c757d;
    font-weight: 600;
  }

  .input-with-symbol .form-control {
    padding-right: 40px;
  }

  /* Tag Styling */
  .tag-input-wrapper {
    position: relative;
  }

  .tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px;
    border: 1px solid #ced4da;
    border-radius: 20px;
    background: #fff;
    min-height: 45px;
    margin-bottom: 8px;
  }

  .tag {
    display: flex;
    align-items: center;
    background: #f0f4ff;
    color: #1e347d;
    padding: 6px 12px;
    border-radius: 15px;
    font-size: 14px;
    font-weight: 500;
  }

  .tag-remove {
    margin-left: 6px;
    cursor: pointer;
    font-size: 16px;
    color: #6c757d;
    font-weight: bold;
  }

  .tag-remove:hover {
    color: #dc3545;
  }

  /* Form Controls */
  .form-control.round-20,
  .form-select.round-20 {
    border-radius: 20px !important;
    border: 1px solid #ced4da;
    padding: 12px 20px;
    font-size: 16px;
  }

  .form-control:focus,
  .form-select:focus {
    border-color: #1e347d;
    box-shadow: 0 0 0 0.2rem rgba(30, 52, 125, 0.25);
    outline: none;
  }

  /* Custom Select Styling */
  .custom-select-wrapper {
    position: relative;
    width: 100%;
  }

  .custom-select {
    border: 1px solid #ced4da;
    border-radius: 20px;
    padding: 12px 20px;
    background: white;
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    border: 1px solid var(--orange) !important;
    color: var(--orange) !important;
    font-weight: 400;
    width: 100%;
    padding: 3px 15px !important;
  }

  .custom-select.disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }

  .custom-select .selected-option {
    flex-grow: 1;
    color: var(--orange) !important;
  }

  .custom-select .arrow {
    font-size: 12px;
    color: #ec621a;
    transition: transform 0.2s ease;
    width: 10px !important;
    height: 15px !important;
  }

  .custom-select .arrow.open {
    transform: rotate(180deg);
  }

  .custom-select-options {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    max-height: 250px;
    overflow-y: auto;
    background: white;
    border: 1px solid #ced4da;
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    z-index: 1002;
    margin-top: 5px;
  }

  .custom-select-options .option {
    padding: 10px 15px;
    cursor: pointer;
    font-size: 16px;
    color: #ec621a;
    border-bottom: 1px solid #f0f0f0;
  }

  .custom-select-options .option:last-child {
    border-bottom: none;
  }

  .custom-select-options .option:hover {
    background: #f0f4ff;
  }

  .custom-select-options .option.loading,
  .custom-select-options .option.info {
    color: #6c757d;
    cursor: default;
    font-style: italic;
    text-align: center;
  }

  .custom-select-options .option.loading:hover,
  .custom-select-options .option.info:hover {
    background: transparent;
  }

  .custom-select-options::-webkit-scrollbar {
    width: 8px;
  }

  .custom-select-options::-webkit-scrollbar-thumb {
    background-color: #6c757d;
    border-radius: 10px;
  }

  .custom-select-options::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  /* Table Styling */
  .table-style-1 {
    width: 100%;
    border-collapse: collapse;
  }

  .table-style-1 th,
  .table-style-1 td {
    padding: 12px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }

  .blue-color {
    color: #1e347d;
  }

  .font-bold {
    font-weight: 600;
  }

  .btn-save {
    background: none;
    border: none;
    color: #1e347d;
    font-size: 18px;
  }

  .max-width-65 {
    max-width: 65%;
  }

  .padd-modal {
    padding: 20px 40px;
  }

  .close-link {
    background: none;
    border: none;
    font-size: 1.5rem;
    color: #6c757d;
  }

  .input-form {
    margin-bottom: 15px;
  }

  .label-control {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
  }

  /* Ensure dropdowns work properly in modal */
  .modal-content {
    overflow: visible;
  }

  .modal-body {
    overflow: visible;
  }

  /* Responsive Design */
  @media (max-width: 768px) {
    .max-width-65 {
      max-width: 95%;
    }

    .padd-modal {
      padding: 20px;
    }

    .rules-section,
    .actions-section,
    .settings-section {
      padding: 1rem;
    }

    .custom-select-options {
      max-height: 200px;
    }
  }

  .search-container {
    padding: 10px;
    border-bottom: 1px solid #f0f0f0;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 1003;
  }

  .search-input {
    border-radius: 15px !important;
    border: 1px solid #ced4da;
    padding: 8px 15px;
    font-size: 14px;
    width: 100%;
  }

  .search-input:focus {
    border-color: #1e347d;
    box-shadow: 0 0 0 0.2rem rgba(30, 52, 125, 0.25);
    outline: none;
  }
</style>
